import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon, ClockIcon } from 'lucide-react';
interface ScorekeeperProps {
  gameMode: string;
  players: string[];
  onFinish: (scores: Record<string, number[]>) => void;
}
// Par values for 9 holes
const PAR_VALUES = [2, 3, 2, 2, 3, 2, 3, 2, 3];
// Challenge definitions for Resort Challenge mode (6 holes)
const CHALLENGES = [{
  name: 'The Opener',
  desc: 'Standard putt. Clean start.',
  multiplier: 1,
  basePoints: 10,
  color: 'text-brand-cream-light'
}, {
  name: 'Wrong Hand',
  desc: 'Putt with your non-dominant hand.',
  multiplier: 2,
  basePoints: 10,
  color: 'text-brand-sand'
}, {
  name: 'The Long Putt',
  desc: 'Extra distance challenge.',
  multiplier: 1,
  basePoints: 15,
  color: 'text-brand-green-light'
}, {
  name: 'Speed Round',
  desc: 'Finish before the timer ends for +5 bonus points.',
  multiplier: 1,
  basePoints: 10,
  color: 'text-red-400',
  hasTimer: true
}, {
  name: 'Pressure Putt',
  desc: 'Must sink in 2 strokes or fewer to earn points.',
  multiplier: 1,
  basePoints: 20,
  color: 'text-purple-400'
}, {
  name: 'The Closer',
  desc: 'Double points! Everything counts 2x.',
  multiplier: 2,
  basePoints: 10,
  color: 'text-brand-gold'
}];
export function Scorekeeper({
  gameMode,
  players,
  onFinish
}: ScorekeeperProps) {
  const TOTAL_HOLES = gameMode === 'challenge' ? 6 : 9;
  const [currentHole, setCurrentHole] = useState(1);
  // Initialize scores: { "Player 1": [0,0,0...], "Player 2": [0,0,0...] }
  // For challenge mode, this stores POINTS. For others, it stores STROKES.
  const [scores, setScores] = useState<Record<string, number[]>>(() => {
    const initial: Record<string, number[]> = {};
    players.forEach(p => {
      initial[p] = Array(TOTAL_HOLES).fill(0);
    });
    return initial;
  });
  // For match play, track holes won
  const [matchStatus, setMatchStatus] = useState({
    p1Wins: 0,
    p2Wins: 0,
    statusText: 'ALL SQUARE'
  });
  // For challenge mode timer
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  // Update match status whenever scores change or hole changes
  useEffect(() => {
    if (gameMode === 'match' && players.length === 2) {
      let p1Wins = 0;
      let p2Wins = 0;
      for (let i = 0; i < currentHole - 1; i++) {
        const s1 = scores[players[0]][i];
        const s2 = scores[players[1]][i];
        if (s1 > 0 && s2 > 0) {
          if (s1 < s2) p1Wins++;else if (s2 < s1) p2Wins++;
        }
      }
      let statusText = 'ALL SQUARE';
      const diff = Math.abs(p1Wins - p2Wins);
      if (p1Wins > p2Wins) statusText = `${diff} UP`;else if (p2Wins > p1Wins) statusText = `${diff} UP`;
      setMatchStatus({
        p1Wins,
        p2Wins,
        statusText
      });
    }
  }, [scores, currentHole, gameMode, players]);
  // Challenge mode timer logic
  useEffect(() => {
    if (gameMode === 'challenge' && CHALLENGES[currentHole - 1]?.hasTimer) {
      setTimeLeft(30);
      setTimerActive(true);
    } else {
      setTimerActive(false);
    }
  }, [currentHole, gameMode]);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);
  const updateScore = (player: string, change: number) => {
    setScores(prev => {
      const newScores = {
        ...prev
      };
      const currentScore = newScores[player][currentHole - 1];
      if (gameMode === 'challenge') {
        // Challenge mode: track strokes, auto-calculate points
        // If unplayed (0) and pressing +, start at par (2)
        if (currentScore === 0 && change > 0) {
          newScores[player][currentHole - 1] = 2;
        } else if (currentScore === 0 && change < 0) {
          // Do nothing if score is 0 and pressing -
        } else {
          const newScore = Math.max(1, currentScore + change);
          newScores[player][currentHole - 1] = newScore;
        }
      } else {
        // Standard stroke adjustment
        if (currentScore === 0 && change < 0) {
          // Do nothing if score is 0 and pressing -
        } else if (currentScore === 0 && change > 0) {
          // First tap: set to par as starting point
          newScores[player][currentHole - 1] = PAR_VALUES[Math.min(currentHole - 1, PAR_VALUES.length - 1)];
        } else {
          const newScore = Math.max(1, currentScore + change);
          newScores[player][currentHole - 1] = newScore;
        }
      }
      return newScores;
    });
  };
  const nextHole = () => {
    if (currentHole < TOTAL_HOLES) {
      setCurrentHole(prev => prev + 1);
    } else {
      onFinish(scores);
    }
  };
  const prevHole = () => {
    if (currentHole > 1) {
      setCurrentHole(prev => prev - 1);
    }
  };
  const getHoleScore = (player: string) => scores[player][currentHole - 1];
  const getTotalScore = (player: string) => scores[player].reduce((a, b) => a + b, 0);
  const getRelativeScore = (player: string) => {
    let totalPar = 0;
    let totalStrokes = 0;
    for (let i = 0; i < currentHole; i++) {
      if (scores[player][i] > 0) {
        totalPar += PAR_VALUES[i];
        totalStrokes += scores[player][i];
      }
    }
    if (totalStrokes === 0) return 'E';
    const diff = totalStrokes - totalPar;
    if (diff === 0) return 'E';
    return diff > 0 ? `+${diff}` : `${diff}`;
  };
  const getScoreColor = (score: number, par: number) => {
    if (score === 0) return 'text-brand-cream-light';
    if (score === 1) return 'text-brand-gold'; // Hole in one
    if (score < par) return 'text-brand-green-light'; // Under par
    if (score === par) return 'text-brand-cream-light'; // Par
    return 'text-red-400'; // Over par
  };
  // Check if all players have a score for the current hole
  const isHoleComplete = players.every(p => scores[p][currentHole - 1] > 0);
  // Render different UIs based on game mode
  const renderStrokePlay = () => <div className="space-y-4" data-id="element-180">
      {players.map(player => {
      const score = getHoleScore(player);
      const par = PAR_VALUES[currentHole - 1];
      const relative = getRelativeScore(player);
      return <div key={player} className="bg-brand-green-deep/40 border border-brand-cream/10 rounded-2xl p-4 flex items-center justify-between" data-id="element-181">
            <div data-id="element-182">
              <h3 className="font-heading text-xl text-brand-cream-light" data-id="element-183">
                {player}
              </h3>
              <div className="flex items-center space-x-2 mt-1" data-id="element-184">
                <p className="text-xs font-body text-brand-cream/50 uppercase tracking-wider" data-id="element-185">
                  Total: {getTotalScore(player) || '-'}
                </p>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${relative === 'E' ? 'bg-brand-cream/10 text-brand-cream/70' : relative.startsWith('+') ? 'bg-red-400/10 text-red-400' : 'bg-brand-green-light/20 text-brand-green-light'}`} data-id="element-186">
                  {relative}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-3" data-id="element-187">
              <button onClick={() => updateScore(player, -1)} className="w-12 h-12 rounded-full bg-brand-cream/5 border border-brand-cream/10 flex items-center justify-center text-brand-cream-light hover:bg-brand-cream/10 active:scale-95 transition-all text-xl" data-id="element-188">
                -
              </button>

              <div className="w-14 text-center" data-id="element-189">
                <span className={`text-4xl font-heading ${getScoreColor(score, par)}`} data-id="element-190">
                  {score || '-'}
                </span>
              </div>

              <button onClick={() => updateScore(player, 1)} className="w-12 h-12 rounded-full bg-brand-cream/5 border border-brand-cream/10 flex items-center justify-center text-brand-cream-light hover:bg-brand-cream/10 active:scale-95 transition-all text-xl" data-id="element-191">
                +
              </button>
            </div>
          </div>;
    })}
    </div>;
  const renderMatchPlay = () => {
    if (players.length !== 2) return null;
    const p1 = players[0];
    const p2 = players[1];
    const s1 = getHoleScore(p1);
    const s2 = getHoleScore(p2);
    const par = PAR_VALUES[currentHole - 1];
    // Tug of war calculation
    const totalHolesPlayed = currentHole - 1;
    const maxLead = Math.max(1, totalHolesPlayed);
    const leadRatio = totalHolesPlayed === 0 ? 0 : (matchStatus.p1Wins - matchStatus.p2Wins) / maxLead;
    const indicatorPosition = 50 + leadRatio * 40; // 10% to 90%
    return <div className="space-y-6" data-id="element-192">
        {/* Match Status Bar */}
        <div className="bg-brand-green-deep/60 rounded-2xl p-4 border border-brand-cream/10" data-id="element-193">
          <div className="flex justify-between items-end mb-2" data-id="element-194">
            <span className={`text-sm font-heading ${matchStatus.p1Wins > matchStatus.p2Wins ? 'text-brand-gold' : 'text-brand-cream/50'}`} data-id="element-195">
              {p1}
            </span>
            <span className="text-lg font-bold text-brand-cream-light tracking-widest" data-id="element-196">
              {matchStatus.statusText}
            </span>
            <span className={`text-sm font-heading ${matchStatus.p2Wins > matchStatus.p1Wins ? 'text-brand-gold' : 'text-brand-cream/50'}`} data-id="element-197">
              {p2}
            </span>
          </div>
          <div className="h-2 bg-brand-charcoal rounded-full relative overflow-hidden" data-id="element-198">
            <div className="absolute inset-y-0 left-0 bg-brand-cream/20 w-1/2" data-id="element-199" />
            <div className="absolute inset-y-0 right-0 bg-brand-cream/10 w-1/2" data-id="element-200" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-3 bg-brand-charcoal z-10" data-id="element-201" />
            <motion.div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-brand-gold rounded-full shadow-[0_0_10px_rgba(184,150,90,0.8)] z-20" animate={{
            left: `${indicatorPosition}%`
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} style={{
            x: '-50%'
          }} data-id="element-202" />
          </div>
        </div>

        {/* Head to Head Scoring */}
        <div className="grid grid-cols-2 gap-4" data-id="element-203">
          {[p1, p2].map((player, idx) => {
          const score = getHoleScore(player);
          const isWinningHole = s1 > 0 && s2 > 0 && (idx === 0 && s1 < s2 || idx === 1 && s2 < s1);
          return <div key={player} className={`bg-brand-green-deep/40 border ${isWinningHole ? 'border-brand-gold' : 'border-brand-cream/10'} rounded-2xl p-4 flex flex-col items-center relative overflow-hidden`} data-id="element-204">
                {isWinningHole && <div className="absolute inset-0 bg-brand-gold/5" data-id="element-205" />}
                <h3 className="font-heading text-lg text-brand-cream-light mb-4 relative z-10" data-id="element-206">
                  {player}
                </h3>

                <div className="flex flex-col items-center space-y-4 relative z-10" data-id="element-207">
                  <button onClick={() => updateScore(player, 1)} className="w-14 h-14 rounded-full bg-brand-cream/5 border border-brand-cream/10 flex items-center justify-center text-brand-cream-light hover:bg-brand-cream/10 active:scale-95 transition-all text-2xl" data-id="element-208">
                    +
                  </button>

                  <div className="h-16 flex items-center justify-center" data-id="element-209">
                    <span className={`text-5xl font-heading ${getScoreColor(score, par)}`} data-id="element-210">
                      {score || '-'}
                    </span>
                  </div>

                  <button onClick={() => updateScore(player, -1)} className="w-14 h-14 rounded-full bg-brand-cream/5 border border-brand-cream/10 flex items-center justify-center text-brand-cream-light hover:bg-brand-cream/10 active:scale-95 transition-all text-2xl" data-id="element-211">
                    -
                  </button>
                </div>
              </div>;
        })}
        </div>

        {/* Hole Result Banner */}
        <AnimatePresence data-id="element-212">
          {s1 > 0 && s2 > 0 && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center py-2" data-id="element-213">
              <span className="text-brand-gold font-heading text-xl" data-id="element-214">
                {s1 === s2 ? 'Hole Halved' : `${s1 < s2 ? p1 : p2} wins hole`}
              </span>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  const renderChallengeMode = () => {
    const challenge = CHALLENGES[currentHole - 1];
    if (!challenge) return null;
    return <div className="space-y-6" data-id="element-215">
        {/* Challenge Card */}
        <div className="bg-brand-green-deep/80 rounded-2xl p-6 border border-brand-cream/10 relative overflow-hidden" data-id="element-216">
          <div className={`absolute top-0 right-0 w-32 h-32 bg-current opacity-5 rounded-bl-full ${challenge.color}`} data-id="element-217" />

          <div className="flex justify-between items-start mb-2 relative z-10" data-id="element-218">
            <h3 className={`text-2xl font-heading ${challenge.color}`} data-id="element-219">
              {challenge.name}
            </h3>
            <div className="bg-brand-charcoal/80 px-2 py-1 rounded text-xs font-bold text-brand-cream-light border border-brand-cream/10" data-id="element-220">
              {challenge.multiplier}X PTS
            </div>
          </div>
          <p className="text-brand-cream/70 font-body text-sm mb-4 relative z-10" data-id="element-221">
            {challenge.desc}
          </p>

          {challenge.hasTimer && <div className="flex items-center space-x-2 bg-brand-charcoal/50 p-3 rounded-xl border border-red-400/20" data-id="element-222">
              <ClockIcon className={`w-5 h-5 ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-red-400'}`} data-id="element-223" />
              <div className="flex-1 bg-brand-charcoal rounded-full h-2 overflow-hidden" data-id="element-224">
                <motion.div className={`h-full ${timeLeft <= 5 ? 'bg-red-500' : 'bg-red-400'}`} initial={{
              width: '100%'
            }} animate={{
              width: `${timeLeft / 30 * 100}%`
            }} transition={{
              duration: 1,
              ease: 'linear'
            }} data-id="element-225" />
              </div>
              <span className={`font-mono font-bold ${timeLeft <= 5 ? 'text-red-500' : 'text-brand-cream-light'}`} data-id="element-226">
                00:{timeLeft.toString().padStart(2, '0')}
              </span>
            </div>}
        </div>

        {/* Players List (Strokes → Points) */}
        <div className="space-y-3" data-id="element-227">
          {players.map(player => {
          const strokes = getHoleScore(player);
          const ch = CHALLENGES[currentHole - 1];
          // Calculate points from strokes
          let pts = 0;
          if (strokes > 0 && ch) {
            const penalty = 2;
            const par = 2;
            pts = Math.max(0, ch.basePoints - (strokes - par) * penalty) * ch.multiplier;
            // Pressure Putt: 0 points if more than 2 strokes
            if (currentHole === 5 && strokes > 2) pts = 0;
          }
          return <div key={player} className="bg-brand-green-deep/40 border border-brand-cream/10 rounded-2xl p-4 flex items-center justify-between" data-id="element-228">
                <div data-id="element-229">
                  <h3 className="font-heading text-lg text-brand-cream-light" data-id="element-230">
                    {player}
                  </h3>
                  <p className="text-[10px] font-body text-brand-cream/50 uppercase tracking-wider mt-1" data-id="element-231">
                    Total: {getTotalScore(player)} strokes
                  </p>
                </div>

                <div className="flex items-center space-x-3" data-id="element-232">
                  <button onClick={() => updateScore(player, -1)} className="w-10 h-10 rounded-full bg-brand-cream/5 border border-brand-cream/10 flex items-center justify-center text-brand-cream-light hover:bg-brand-cream/10 active:scale-95 transition-all" data-id="element-233">
                    -
                  </button>

                  <div className="w-16 text-center" data-id="element-234">
                    <span className="text-3xl font-heading text-brand-cream-light" data-id="element-235">
                      {strokes || '-'}
                    </span>
                    {strokes > 0 && <span className="text-[10px] block text-brand-gold font-bold" data-id="element-236">
                        {pts} pts
                      </span>}
                  </div>

                  <button onClick={() => updateScore(player, 1)} className="w-10 h-10 rounded-full bg-brand-cream/5 border border-brand-cream/10 flex items-center justify-center text-brand-cream-light hover:bg-brand-cream/10 active:scale-95 transition-all" data-id="element-237">
                    +
                  </button>
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  const renderBestBall = () => {
    if (players.length !== 4) return null;
    const team1 = [players[0], players[1]];
    const team2 = [players[2], players[3]];
    const par = PAR_VALUES[currentHole - 1];
    const getTeamBest = (team: string[]) => {
      const s1 = getHoleScore(team[0]);
      const s2 = getHoleScore(team[1]);
      if (s1 === 0 && s2 === 0) return 0;
      if (s1 === 0) return s2;
      if (s2 === 0) return s1;
      return Math.min(s1, s2);
    };
    const t1Best = getTeamBest(team1);
    const t2Best = getTeamBest(team2);
    const renderTeam = (team: string[], teamNum: number, colorClass: string, bgClass: string) => <div className={`bg-brand-green-deep/40 border border-brand-cream/10 rounded-2xl overflow-hidden mb-4`} data-id="element-238">
        <div className={`${bgClass} px-4 py-2 flex justify-between items-center`} data-id="element-239">
          <span className={`text-xs font-bold uppercase tracking-widest ${colorClass}`} data-id="element-240">
            Team {teamNum}
          </span>
          <span className="text-brand-cream-light font-heading text-lg" data-id="element-241">
            Best: {getTeamBest(team) || '-'}
          </span>
        </div>

        <div className="p-2 space-y-2" data-id="element-242">
          {team.map(player => {
          const score = getHoleScore(player);
          const isBest = score > 0 && score === getTeamBest(team);
          return <div key={player} className={`p-3 rounded-xl flex items-center justify-between ${isBest ? 'bg-brand-cream/5 border border-brand-cream/10' : 'border border-transparent'}`} data-id="element-243">
                <h3 className={`font-heading text-lg ${isBest ? 'text-brand-cream-light' : 'text-brand-cream/70'}`} data-id="element-244">
                  {player}
                </h3>

                <div className="flex items-center space-x-2" data-id="element-245">
                  <button onClick={() => updateScore(player, -1)} className="w-10 h-10 rounded-full bg-brand-cream/5 flex items-center justify-center text-brand-cream-light active:scale-95" data-id="element-246">
                    -
                  </button>
                  <div className="w-10 text-center" data-id="element-247">
                    <span className={`text-2xl font-heading ${getScoreColor(score, par)}`} data-id="element-248">
                      {score || '-'}
                    </span>
                  </div>
                  <button onClick={() => updateScore(player, 1)} className="w-10 h-10 rounded-full bg-brand-cream/5 flex items-center justify-center text-brand-cream-light active:scale-95" data-id="element-249">
                    +
                  </button>
                </div>
              </div>;
        })}
        </div>
      </div>;
    return <div className="space-y-2" data-id="element-250">
        {/* Team Totals Header */}
        <div className="flex justify-between items-center px-4 py-2 mb-2" data-id="element-251">
          <div className="text-center" data-id="element-252">
            <span className="text-[10px] text-brand-gold uppercase tracking-widest block" data-id="element-253">
              T1 Total
            </span>
            <span className="text-2xl font-heading text-brand-cream-light" data-id="element-254">
              {team1.reduce((total, p) => total + getTotalScore(p), 0)}{' '}
              {/* Simplified total for demo */}
            </span>
          </div>
          <div className="text-center" data-id="element-255">
            <span className="text-[10px] text-brand-green-light uppercase tracking-widest block" data-id="element-256">
              T2 Total
            </span>
            <span className="text-2xl font-heading text-brand-cream-light" data-id="element-257">
              {team2.reduce((total, p) => total + getTotalScore(p), 0)}
            </span>
          </div>
        </div>

        {renderTeam(team1, 1, 'text-brand-gold', 'bg-brand-gold/10')}
        {renderTeam(team2, 2, 'text-brand-green-light', 'bg-brand-green-light/10')}
      </div>;
  };
  return <div className="flex flex-col h-full bg-brand-charcoal" data-id="element-258">
      {/* Hole Header */}
      <div className="bg-brand-green-deep pt-8 pb-6 px-6 rounded-b-3xl shadow-lg relative z-10" data-id="element-259">
        <div className="flex items-center justify-between mb-2" data-id="element-260">
          <button onClick={prevHole} className={`p-2 rounded-full transition-colors ${currentHole > 1 ? 'bg-brand-cream/10 text-brand-cream-light hover:bg-brand-cream/20' : 'opacity-0 pointer-events-none'}`} data-id="element-261">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-262" />
          </button>

          <div className="text-center" data-id="element-263">
            {gameMode !== 'challenge' && <span className="text-brand-gold text-xs font-bold uppercase tracking-widest block mb-1" data-id="element-264">
                Par {PAR_VALUES[currentHole - 1]}
              </span>}
            <h2 className="text-4xl font-heading text-brand-cream-light" data-id="element-265">
              Hole {currentHole}
            </h2>
          </div>

          <button onClick={nextHole} className={`p-2 rounded-full transition-colors ${currentHole < TOTAL_HOLES ? 'bg-brand-cream/10 text-brand-cream-light hover:bg-brand-cream/20' : 'opacity-0 pointer-events-none'}`} data-id="element-266">
            <ChevronRightIcon className="w-5 h-5" data-id="element-267" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center space-x-1.5 mt-6" data-id="element-268">
          {Array.from({
          length: TOTAL_HOLES
        }).map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i + 1 === currentHole ? 'w-6 bg-brand-gold' : i + 1 < currentHole ? 'w-1.5 bg-brand-gold/50' : 'w-1.5 bg-brand-cream/20'}`} data-id="element-269" />)}
        </div>
      </div>

      {/* Dynamic Content Area */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-270">
        <AnimatePresence mode="wait" data-id="element-271">
          <motion.div key={currentHole} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-272">
            {gameMode === 'stroke' && renderStrokePlay()}
            {gameMode === 'match' && renderMatchPlay()}
            {gameMode === 'challenge' && renderChallengeMode()}
            {gameMode === 'bestball' && renderBestBall()}
            {/* Fallback if mode is empty */}
            {!['stroke', 'match', 'challenge', 'bestball'].includes(gameMode) && renderStrokePlay()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Action */}
      <div className="p-6 pt-2 bg-gradient-to-t from-brand-charcoal via-brand-charcoal to-transparent" data-id="element-273">
        <button onClick={nextHole} className={`w-full py-5 rounded-xl font-heading text-xl transition-all active:scale-[0.98] flex items-center justify-center space-x-2 ${isHoleComplete ? 'bg-brand-gold text-brand-charcoal shadow-lg hover:bg-brand-gold-light' : 'bg-brand-cream/10 text-brand-cream-light border border-brand-cream/20 hover:bg-brand-cream/20'}`} data-id="element-274">
          {currentHole === TOTAL_HOLES ? <>
              <span data-id="element-275">Finish Round</span>
              <CheckIcon className="w-5 h-5" data-id="element-276" />
            </> : <span data-id="element-277">{isHoleComplete ? 'Next Hole' : 'Skip to Next'}</span>}
        </button>
      </div>
    </div>;
}